import React from "react";
import PropTypes from "prop-types";
import { MODAL_SIZE, ModalForm } from "components/Modal";
import { SIZE, TYPE } from "components/Form";
import { FormattedMessage } from "react-intl";
import { MUTATION_TYPE, useToast } from "hooks";
import { ADD_INTERVIEW_QUESTION, UPDATE_INTERVIEW_QUESTION } from "graphql/mutations";
import { convertFromPlainText, getPlainText } from "components/Form/Editor";
import { GET_MISSION_ROLE_INTERVIEW } from "graphql/queries";
import { INTERVIEW_QUESTION_TIME_LIMIT_OPTIONS } from "constants";

/**
 * RoleInterviewQuestionForm
 *
 * @param {Object}    question
 * @param {Object}    missionRole
 * @param {Boolean}   show
 * @param {Function}  onClose
 */
const RoleInterviewQuestionForm = ({ missionRole, question, show, onClose, ...props }) => {
  const { toast } = useToast();
  const isEdit = !!question;

  const handleComplete = () => {
    toast.success(<FormattedMessage id={`RoleInterviewQuestionForm.${isEdit ? "Edit" : ""}FormSuccess`} />);
  };

  const handleError = () => {
    toast.error(<FormattedMessage id={`RoleInterviewQuestionForm.${isEdit ? "Edit" : ""}FormError`} />);
  };

  return (
    <ModalForm
      show={show}
      data={FORM_DATA(isEdit)}
      onClose={onClose}
      onCompleted={handleComplete}
      onError={handleError}
      title={<FormattedMessage id={`RoleInterviewQuestionForm.${isEdit ? "Edit" : ""}FormTitle`} />}
      size={MODAL_SIZE.medium}
      mutationType={isEdit ? MUTATION_TYPE.update : MUTATION_TYPE.add}
      parentKeyName="missionRoleId"
      parentKeyValue={missionRole?.id}
      updateId={question?.id}
      initialValues={{
        title: question?.title,
        descriptionJson: question?.descriptionJson ?? convertFromPlainText(question?.description),
        timeLimitSeconds: question?.timeLimitSeconds,
      }}
      mutationData={{
        mutationName: {
          add: ADD_INTERVIEW_QUESTION,
          update: UPDATE_INTERVIEW_QUESTION,
        },
        refetchAfterMutate: true,
        refetchQueries: [GET_MISSION_ROLE_INTERVIEW],
      }}
      {...props}
    />
  );
};

const FORM_DATA = (isEdit) => [
  {
    items: [
      !isEdit && {
        type: TYPE.fileUpload,
        size: SIZE.xlarge,
        properties: {
          name: "video",
          required: false,
          accept: "video/*",
          uploadMessageId: "RoleInterviewQuestionForm.VideoUploadButtonText",
        },
      },
      {
        label: "Title",
        type: TYPE.input,
        size: SIZE.large,
        properties: {
          name: "title",
          required: true,
          autoFocus: true,
        },
      },
      {
        label: "Answer Length",
        type: TYPE.select,
        size: SIZE.small,
        properties: {
          name: "timeLimitSeconds",
          required: true,
          options: INTERVIEW_QUESTION_TIME_LIMIT_OPTIONS,
        },
      },
      {
        label: "Question",
        type: TYPE.editor,
        size: SIZE.xlarge,
        onChange: (name, value, handleOnChange) => {
          handleOnChange("description", getPlainText(value));
          handleOnChange(name, value);
        },
        properties: {
          name: "descriptionJson",
          required: true,
          showFormattingOptions: true,
        },
      },
    ].filter(Boolean),
  },
];

RoleInterviewQuestionForm.propTypes = {
  missionRole: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  question: PropTypes.object,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RoleInterviewQuestionForm;
